import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  EntryReportReason,
  SubmitReportRequest,
  submitEntryReport,
} from "../../services/entryReportService";
import "./ReportModal.css";

interface ReportModalProps {
  entryId: number;
  onClose: () => void;
  /** Called after a successful submission so the parent can mark the button as already-reported */
  onSuccess: () => void;
}

// Ordered list of report reasons shown in the modal
const REPORT_REASONS: EntryReportReason[] = [
  "SPAM",
  "PROFANITY",
  "HATE_SPEECH",
  "HARASSMENT",
  "MISINFORMATION",
  "PERSONAL_INFO",
  "INAPPROPRIATE",
  "AI_GENERATED",
  "SPELLING_ERROR",
  "NOT_A_DEFINITION",
  "OTHER",
];

/**
 * Modal for reporting an entry.
 * Rendered into a portal so it sits above all other content.
 *
 * Flow:
 *  1. User picks a reason from the radio list
 *  2. If "OTHER" is selected, a textarea appears for free-text input
 *  3. Submit → POST /api/entries/:id/report
 *  4. On success: show inline success message, then close after 1.5s
 *  5. On ALREADY_REPORTED error: show localized error inline
 */
export const ReportModal: React.FC<ReportModalProps> = ({
  entryId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [selectedReason, setSelectedReason] = useState<EntryReportReason | null>(null);
  const [otherText, setOtherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;
    if (selectedReason === "OTHER" && !otherText.trim()) {
      setErrorMsg(t("report_error_other_required"));
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    const payload: SubmitReportRequest = {
      reason: selectedReason,
      otherReason: selectedReason === "OTHER" ? otherText.trim() : undefined,
    };

    try {
      await submitEntryReport(entryId, payload);
      setSubmitted(true);
      onSuccess();
      // Auto-close after showing the success message
      setTimeout(onClose, 1500);
    } catch (err: any) {
      // apiClient throws new Error(message) — message comes directly from backend response body
      const code: string = err?.message || "";
      if (code === "ALREADY_REPORTED") {
        setErrorMsg(t("report_error_already_reported"));
      } else if (code === "REPORT_OWN_ENTRY") {
        setErrorMsg(t("report_error_own_entry"));
      } else {
        setErrorMsg(t("report_error_generic"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    selectedReason !== null &&
    (selectedReason !== "OTHER" || otherText.trim().length > 0);

  return ReactDOM.createPortal(
    <div
      className="report-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={t("report_modal_title", { id: entryId })}
    >
      <div
        className="report-modal"
      >
        {/* Header */}
        <div className="report-modal__header">
          <h3 className="report-modal__title">
            #{entryId} {t("report_modal_heading")}
          </h3>
          <button
            className="report-modal__close"
            onClick={onClose}
            aria-label={t("report_modal_close")}
            type="button"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="report-modal__body">
          {submitted ? (
            <div className="report-modal__success">
              {t("report_success")}
            </div>
          ) : (
            <>
              <p className="report-modal__section-label">
                {t("report_reason_label")}
              </p>

              <ul className="report-modal__reasons">
                {REPORT_REASONS.map((reason) => (
                  <li key={reason} className="report-modal__reason-item">
                    <label className="report-modal__reason-label">
                      <input
                        type="radio"
                        name="report-reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={() => {
                          setSelectedReason(reason);
                          setErrorMsg("");
                        }}
                        className="report-modal__radio"
                      />
                      <span>{t(`report_reason_${reason.toLowerCase()}`)}</span>
                    </label>
                  </li>
                ))}
              </ul>

              {/* Free-text area — visible only when OTHER is selected */}
              {selectedReason === "OTHER" && (
                <div className="report-modal__other-wrapper">
                  <textarea
                    className="report-modal__other-textarea"
                    placeholder={t("report_other_placeholder")}
                    value={otherText}
                    onChange={(e) => {
                      setOtherText(e.target.value);
                      setErrorMsg("");
                    }}
                    maxLength={500}
                    rows={3}
                  />
                  <span className="report-modal__charcount">
                    {otherText.length} / 500
                  </span>
                </div>
              )}

              {errorMsg && (
                <p className="report-modal__error">{errorMsg}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="report-modal__footer">
            <button
              className="report-modal__btn report-modal__btn--cancel"
              onClick={onClose}
              type="button"
              disabled={isSubmitting}
            >
              {t("report_cancel")}
            </button>
            <button
              className="report-modal__btn report-modal__btn--submit"
              onClick={handleSubmit}
              type="button"
              disabled={!canSubmit}
            >
              {isSubmitting ? "..." : t("report_submit")}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};